import React, { useEffect, useState, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { usePDF } from 'react-to-pdf';
import { getAPI } from '../../../network';
import { convertToTimezone } from '../../../../utils/helper';
import {
    Box,
    Typography,
    CircularProgress,
    Alert,
    TableContainer,
    Table,
    TableHead,
    TableBody,
    TableRow,
    TableCell,
    Paper,
    Button,
    Avatar,
    Stack,
    Grid,
} from '@mui/material';
import AttachmentIcon from '@mui/icons-material/Attachment';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';

const ReportDetails = () => {
    const { id } = useParams();
    const [populatedReport, setPopulatedReport] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [isGeneratingPDF, setIsGeneratingPDF] = useState(false);

    const {
        reportTypeId,
        siteId,
        userId,
        questions = [],
        note,
        media,
    } = populatedReport || {};
    
    const { toPDF, targetRef } = usePDF({
        filename: reportTypeId?.name ? `${reportTypeId?.name}-${id}.pdf` : `Report-${id}.pdf`,
        page: { margin: 10 },
        overrides: {
            pdf: {
                info: {
                    Title: `Report ${id}`,
                    Author: 'Secuber',
                },
            },
        },
    });

    useEffect(() => {
        const fetchReport = async () => {
            try {
                const response = await getAPI(`/reports/details/${id}`);
                if (response) {
                    setPopulatedReport(response);
                }
            } catch (err) {
                console.error('Failed to fetch report:', err);
                setError(err.message || 'Failed to load report.');
            } finally {
                setLoading(false);
            }
        };

        fetchReport();
    }, [id]);

    const reportedDateTime = useMemo(
        () =>
            convertToTimezone(
                populatedReport?.updatedAt,
                populatedReport?.userId?.timeZone
            ) || '',
        [populatedReport?.updatedAt, populatedReport?.userId?.timeZone]
    );

    if (loading) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
                <CircularProgress />
            </Box>
        );
    }

    if (error) {
        return (
            <Alert severity="error" sx={{ margin: 2 }}>
                {error}
            </Alert>
        );
    }

    return (
        <Box sx={{ p: 3, maxWidth: 900, margin: '0 auto' }}>
            {/* Header with Export Button */}
            <Stack direction="row" justifyContent="space-between" alignItems="center" mb={3}>
                <Typography variant="h5" fontWeight="bold">
                    Report Details
                </Typography>
                <Button
                    variant="contained"
                    startIcon={isGeneratingPDF ? <CircularProgress size={20} /> : <PictureAsPdfIcon />}
                    onClick={() => {
                        setIsGeneratingPDF(true);
                        toPDF().finally(() => setIsGeneratingPDF(false));
                    }}
                    disabled={isGeneratingPDF}
                    sx={{
                        bgcolor: 'error.main',
                        '&:hover': { bgcolor: 'error.dark' },
                        minWidth: 140,
                    }}
                >
                    {isGeneratingPDF ? 'Generating...' : 'Export PDF'}
                </Button>
            </Stack>

            {/* PDF Content */}
            <Box ref={targetRef} sx={{ bgcolor: '#fff', p: 3, borderRadius: 2, boxShadow: 3 }}>
                {/* Report Header */}
                <Paper elevation={1} sx={{ p: 3, mb: 3 }}>
                    <Box display="flex" justifyContent="space-between" alignItems="center">
                        <Box>
                            <Typography variant="h6" fontWeight="bold" gutterBottom>
                                Report Name
                            </Typography>
                            <Typography>{reportTypeId?.name || 'N/A'}</Typography>
                        </Box>
                        <Avatar
                            src="/logo.png"
                            alt="Secuber Logo"
                            sx={{ width: 180, height: 90 }}
                            variant="square"
                        />
                    </Box>

                    <Grid container spacing={2} mt={3}>
                        <Grid item xs={12} sm={4}>
                            <Typography fontWeight="bold">Site Name</Typography>
                            <br />
                            <Typography>{siteId?.name || 'N/A'}</Typography>
                        </Grid>
                        <Grid item xs={12} sm={4}>
                            <Typography fontWeight="bold">Reported Date & Time</Typography>
                            <br />
                            <Typography>{reportedDateTime}</Typography>
                        </Grid>
                        <Grid item xs={12} sm={4}>
                            <Typography fontWeight="bold">Submitted By</Typography>
                            <br />
                            <Typography>
                                {userId?.firstname || ''} {userId?.lastname || ''}
                            </Typography>
                        </Grid>
                    </Grid>
                </Paper>

                {/* Questions Table */}
                <Typography variant="h6" fontWeight="bold" sx={{ mb: 2 }}>
                    Questions
                </Typography>

                <TableContainer component={Paper} sx={{ mb: 3 }}>
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableCell><strong>Question</strong></TableCell>
                                <TableCell><strong>Answer</strong></TableCell>
                                <TableCell><strong>Comment</strong></TableCell>
                                <TableCell><strong>Media</strong></TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {questions.length > 0 ? questions.map((q) => (
                                <TableRow key={q._id || q.questionId?._id}>
                                    <TableCell>{q.questionId?.question || 'N/A'}</TableCell>
                                    <TableCell>{q.answer || 'N/A'}</TableCell>
                                    <TableCell>{q.comment || 'N/A'}</TableCell>
                                    <TableCell>
                                        {q.media ? (
                                            <Box display="flex" flexDirection="column" alignItems="flex-start" gap={1}>
                                                <img
                                                    src={q.media}
                                                    alt="Media"
                                                    style={{
                                                        width: 80,
                                                        height: 80,
                                                        objectFit: 'cover',
                                                        borderRadius: 6,
                                                        border: '1px solid #ccc',
                                                    }}
                                                />
                                                <Typography
                                                    variant="body2"
                                                    color="primary"
                                                    component="a"
                                                    href={q.media}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    sx={{
                                                        wordBreak: 'break-all',
                                                        textDecoration: 'underline',
                                                    }}
                                                >
                                                    {q.media}
                                                </Typography>
                                            </Box>
                                        ) : (
                                            'N/A'
                                        )}
                                    </TableCell>

                                </TableRow>
                            )) : (
                                <TableRow>
                                    <TableCell colSpan={4} align="center">
                                        No questions available.
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>

                {/* Overall Notes Section */}
                {(note || media) && (
                    <Paper elevation={1} sx={{ p: 3, mb: 2 }}>
                        <Typography variant="h6" fontWeight="bold" gutterBottom>
                            Overall Notes
                        </Typography>
                        {note && (
                            <Typography paragraph sx={{ mb: 2 }}>
                                {note}
                            </Typography>
                        )}
                        {media && (
                            <img
                                src={media}
                                alt="Overall Notes"
                                style={{ maxWidth: '100%', borderRadius: 4 }}
                            />
                        )}
                    </Paper>
                )}

                {/* Footer */}
                <Typography variant="body2" color="text.secondary" textAlign="center" mt={4}>
                    This is a system generated report from Secuber.
                </Typography>
            </Box>
        </Box>
    );
};

export default ReportDetails;
